import { User, AuthSession, DEFAULT_ADMIN_PERMISSIONS, DEFAULT_STAFF_PERMISSIONS, DEFAULT_BOXER_PERMISSIONS, DEFAULT_SPONSOR_PERMISSIONS, DEFAULT_LEGAL_TEAM_PERMISSIONS, Permissions } from '@/types/auth';

const SESSION_KEY = 'lsba_auth_session';
const API_BASE_URL = '/api/auth';

export const authService = {
  // Initialize - no longer needed as backend handles first-time setup
  async initialize(): Promise<void> {
    // Backend automatically creates admin user on first login
    console.log('✅ Auth service initialized - admin account will be created on first login');
  },

  // Get permissions for a role
  getPermissionsForRole(role: string): Permissions {
    switch (role) {
      case 'admin':
        return DEFAULT_ADMIN_PERMISSIONS;
      case 'boxer':
        return DEFAULT_BOXER_PERMISSIONS;
      case 'sponsor':
        return DEFAULT_SPONSOR_PERMISSIONS;
      case 'legal-team':
        return DEFAULT_LEGAL_TEAM_PERMISSIONS;
      default:
        return DEFAULT_STAFF_PERMISSIONS;
    }
  },

  // Login via API
  async login(username: string, password: string): Promise<AuthSession | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      
      // Get full permissions based on role
      const permissions = this.getPermissionsForRole(data.user.role);
      
      const session: AuthSession = {
        user: {
          ...data.user,
          permissions,
        },
        loginTime: data.loginTime,
      };

      localStorage.setItem(SESSION_KEY, JSON.stringify(session));
      return session;
    } catch (error) {
      console.error('Login error:', error);
      return null;
    }
  },

  // Logout
  logout(): void {
    localStorage.removeItem(SESSION_KEY);
  },

  // Get current session
  getSession(): AuthSession | null {
    const sessionData = localStorage.getItem(SESSION_KEY);
    if (!sessionData) return null;
    
    try {
      return JSON.parse(sessionData);
    } catch {
      return null;
    }
  },

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return this.getSession() !== null;
  },

  // Check if user is admin
  isAdmin(): boolean {
    const session = this.getSession();
    return session?.user.role === 'admin';
  },

  // Get all users (admin only) via API
  async getAllUsers(): Promise<User[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/users`);
      if (!response.ok) {
        return [];
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching users:', error);
      return [];
    }
  },

  // Create new user via API
  async createUser(username: string, password: string, role: 'admin' | 'staff' | 'boxer' | 'sponsor' | 'legal-team', linkedEntityId?: string): Promise<User | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password, role, linkedEntityId }),
      });

      if (!response.ok) {
        return null;
      }

      const user = await response.json();
      return {
        ...user,
        permissions: this.getPermissionsForRole(role),
      };
    } catch (error) {
      console.error('Error creating user:', error);
      return null;
    }
  },

  // Delete user via API
  async deleteUser(username: string): Promise<void> {
    try {
      await fetch(`${API_BASE_URL}/users?username=${encodeURIComponent(username)}`, {
        method: 'DELETE',
      });
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  },

  // Update user password via API
  async updatePassword(username: string, newPassword: string): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE_URL}/users`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password: newPassword }),
      });

      return response.ok;
    } catch (error) {
      console.error('Error updating password:', error);
      return false;
    }
  },

  // Update user role via API
  async updateUserRole(username: string, newRole: 'admin' | 'staff' | 'boxer' | 'sponsor' | 'legal-team'): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE_URL}/users`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, role: newRole }),
      });

      return response.ok;
    } catch (error) {
      console.error('Error updating role:', error);
      return false;
    }
  },

  // Update user permissions (note: permissions are now role-based)
  async updateUserPermissions(username: string, permissions: Permissions): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE_URL}/users`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, permissions }),
      });

      return response.ok;
    } catch (error) {
      console.error('Error updating permissions:', error);
      return false;
    }
  },

  // Get user permissions
  async getUserPermissions(userId?: string): Promise<Permissions> {
    const session = this.getSession();
    if (!session) return DEFAULT_STAFF_PERMISSIONS;
    
    return session.user.permissions || this.getPermissionsForRole(session.user.role);
  },

  // Update session (no longer needed - data comes from API)
  async updateSession(): Promise<void> {
    // Session updates happen on login
    console.warn('Session updates now happen on login');
  },

  // Save user (legacy compatibility - now uses API)
  async saveUser(user: User): Promise<void> {
    if (user.username && user.password) {
      await this.createUser(user.username, user.password, user.role, user.linkedEntityId);
    }
  },
};
